// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  customer/src/pages/Cart.jsx  —  Quickry
//  Copy to: C:\quickry\customer\src\pages\Cart.jsx
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Minus, Trash2, ChevronRight,
  MapPin, Clock, ShoppingBag, BadgePercent, Check,
  ChevronDown, X, Bike, Shield, Gift, AlertCircle,
  Banknote, UtensilsCrossed,
  Home as HomeIcon, Building2, Navigation, CheckCircle2,
  Loader2, Info, LocateFixed, Briefcase, Wallet,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const T = {
  primary:"#f97316", primaryLight:"#fff7ed", primaryMid:"#fed7aa", primaryDark:"#ea580c",
  bg:"#f2f2f7", white:"#ffffff", text:"#1c1c1e", textSub:"#6b7280", textMuted:"#9ca3af",
  border:"#e5e7eb", green:"#16a34a", greenLight:"#dcfce7", red:"#ef4444",
  yellow:"#fbbf24", purple:"#7c3aed", blue:"#2563eb", blueLight:"#eff6ff",
};

const API = typeof window !== "undefined" && window.location.hostname !== "localhost"
  ? "https://quickry-ivrv.onrender.com/api"
  : "http://localhost:5000/api";

// COUPONS ab API se fetch hote hain — hardcoded nahi

const PAYMENT_METHODS = [
  { id:"cod",    label:"Cash on Delivery", icon:Banknote, sub:"Pay when order arrives" },
  { id:"wallet", label:"Quickry Wallet",   icon:Wallet,   sub:"Instant • No extra charges" },
];

const ADDR_TYPES = [
  { key:"Home",  Icon:HomeIcon,  label:"Home"  },
  { key:"Work",  Icon:Briefcase, label:"Work"  },
  { key:"Other", Icon:Navigation,label:"Other" },
];

// ── Smart locality parser ─────────────────────────────────────
// GPS se aane wale raw Nominatim data ko clean "locality, city" format mein convert karta hai
function parseLocality(nominatimAddress) {
  const a = nominatimAddress || {};

  // Priority order — jo sabse specific locality info deta hai
  const locality = (
    a.neighbourhood   ||   // e.g. "Ramdaspeth"
    a.suburb          ||   // e.g. "Civil Lines"
    a.quarter         ||   // e.g. "Sindhi Camp"
    a.city_district   ||   // e.g. "Akola District"
    a.village         ||   // rural areas ke liye
    a.town            ||   // small towns ke liye
    ""
  ).trim();

  const road = (a.road || a.pedestrian || a.footway || "").trim();
  const city = (a.city || a.town || a.county || "Akola").trim();

  // Build clean display string — NO coordinates, NO raw IDs
  if (locality && road) return `${road}, ${locality}, ${city}`;
  if (locality)          return `${locality}, ${city}`;
  if (road)              return `${road}, ${city}`;
  return city;
}

// ── Helpers ───────────────────────────────────────────────────
function VegDot({ isVeg }) {
  return (
    <div style={{ width:13, height:13, border:`1.5px solid ${isVeg?T.green:T.red}`, borderRadius:2, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
      <div style={{ width:6, height:6, borderRadius:"50%", background:isVeg?T.green:T.red }}/>
    </div>
  );
}

function Card({ children, style={} }) {
  return (
    <div style={{ background:T.white, borderRadius:16, border:`1px solid ${T.border}`, overflow:"hidden", marginBottom:10, boxShadow:"0 1px 6px rgba(0,0,0,0.04)", ...style }}>
      {children}
    </div>
  );
}

function SLabel({ children }) {
  return (
    <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted, letterSpacing:"1.5px", textTransform:"uppercase", padding:"14px 16px 6px" }}>
      {children}
    </div>
  );
}

// ── Order Placed Screen ───────────────────────────────────────
function OrderPlaced({ restaurantName, onGoHome, onTrack }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 600);
    const t2 = setTimeout(() => setStep(2), 1400);
    const t3 = setTimeout(() => setStep(3), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const steps = [
    { label:"Order Confirmed",      icon:CheckCircle2,    color:T.green   },
    { label:"Preparing your order", icon:UtensilsCrossed, color:T.primary },
    { label:"Rider assigned",       icon:Bike,            color:T.purple  },
  ];

  return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px 16px", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @keyframes popIn  { 0%{transform:scale(0.4);opacity:0} 70%{transform:scale(1.12)} 100%{transform:scale(1);opacity:1} }
        @keyframes pulseS { 0%,100%{transform:scale(1)} 50%{transform:scale(1.06)} }
        .pop-in  { animation:popIn .5s ease both; }
        .pulse-s { animation:pulseS 2s ease-in-out infinite; }
      `}</style>

      <div className="pop-in" style={{ width:88, height:88, borderRadius:"50%", background:T.greenLight, border:`3px solid ${T.green}`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:20 }}>
        <CheckCircle2 size={44} color={T.green} strokeWidth={2}/>
      </div>

      <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.7rem", letterSpacing:"-1px", color:T.text, marginBottom:6, textAlign:"center" }}>
        Order Placed!
      </div>
      <div style={{ color:T.textSub, fontSize:"0.86rem", textAlign:"center", marginBottom:26, lineHeight:1.7 }}>
        {restaurantName && <><strong style={{ color:T.text }}>{restaurantName}</strong><br/></>}
        Your order is being prepared
      </div>

      <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:16, padding:"18px 20px", width:"100%", maxWidth:360, marginBottom:18 }}>
        <div style={{ fontSize:"0.62rem", fontWeight:700, color:T.textMuted, letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:14 }}>Order Status</div>
        {steps.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          return (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:i<2?14:0 }}>
              <div style={{ width:34, height:34, borderRadius:"50%", background:done?s.color:T.border, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"background .4s" }}>
                <Icon size={16} color={done?"#fff":T.textMuted} strokeWidth={2}/>
              </div>
              <div style={{ fontWeight:done?700:500, fontSize:"0.85rem", color:done?T.text:T.textMuted, transition:"color .4s" }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="pulse-s" style={{ background:T.primaryLight, border:`1.5px solid ${T.primaryMid}`, borderRadius:14, padding:"13px 20px", display:"flex", alignItems:"center", gap:12, marginBottom:22, width:"100%", maxWidth:360 }}>
        <Clock size={20} color={T.primary}/>
        <div>
          <div style={{ fontWeight:800, fontSize:"1rem", color:T.primary }}>~28–35 minutes</div>
          <div style={{ fontSize:"0.72rem", color:T.textSub }}>Estimated delivery time</div>
        </div>
      </div>

      <div style={{ display:"flex", gap:10, width:"100%", maxWidth:360 }}>
        <button onClick={onTrack} style={{ flex:1, background:T.primary, color:"#fff", border:"none", borderRadius:12, padding:"13px", fontWeight:700, fontSize:"0.9rem", cursor:"pointer", fontFamily:"inherit", boxShadow:"0 6px 20px rgba(249,115,22,0.28)" }}>
          Track Order
        </button>
        <button onClick={onGoHome} style={{ flex:1, background:T.white, color:T.text, border:`1.5px solid ${T.border}`, borderRadius:12, padding:"13px", fontWeight:700, fontSize:"0.9rem", cursor:"pointer", fontFamily:"inherit" }}>
          Home
        </button>
      </div>
    </div>
  );
}

// ── Address Bottom Sheet ──────────────────────────────────────
function AddressSheet({ onClose, onConfirm, initialAddress }) {
  const [addrType,   setAddrType]   = useState(initialAddress?.type    || "Home");
  const [house,      setHouse]      = useState(initialAddress?.house   || "");
  const [area,       setArea]       = useState(initialAddress?.area    || "");
  const [landmark,   setLandmark]   = useState(initialAddress?.landmark|| "");
  const [mobile,     setMobile]     = useState(initialAddress?.mobile  || "");
  const [locating,   setLocating]   = useState(false);
  const [errors,     setErrors]     = useState({});
  const [userLat,    setUserLat]    = useState(initialAddress?.lat  || null);
  const [userLng,    setUserLng]    = useState(initialAddress?.lng  || null);

  // ✅ Auto-fetch GPS when sheet opens
  useEffect(() => {
    if (!area) fetchGPS();
  }, []);

  const fetchGPS = () => {
    if (!navigator.geolocation) { alert("Location not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res  = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json&addressdetails=1`
          );
          const data = await res.json();

          // ✅ Smart locality parsing — NO coordinates shown to user
          const parsed = parseLocality(data.address);
          setArea(parsed);
          setUserLat(pos.coords.latitude);
          setUserLng(pos.coords.longitude);
        } catch {
          alert("Location fetch failed. Please type manually.");
        }
        setLocating(false);
      },
      () => { alert("Location permission denied"); setLocating(false); }
    );
  };

  const handleConfirm = () => {
    const e = {};
    if (!house.trim())  e.house  = "House/Flat no. required";
    if (!area.trim())   e.area   = "Area required";
    if (!mobile.trim()) e.mobile = "Mobile number required";
    else if (!/^[6-9]\d{9}$/.test(mobile.trim())) e.mobile = "Valid 10-digit mobile number required";
    if (Object.keys(e).length) { setErrors(e); return; }

    // Haversine distance from user GPS to Akola city center
    let distKm = 3; // default fallback
    if (userLat && userLng) {
      const R = 6371;
      const lat1 = userLat * Math.PI / 180;
      const lat2 = 20.7002  * Math.PI / 180; // Akola center lat
      const dLat = (20.7002 - userLat) * Math.PI / 180;
      const dLng = (77.0082 - userLng) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)**2;
      distKm = Math.max(1, Math.round(2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))));
    }

    onConfirm({
      type:     addrType,
      house:    house.trim(),
      area:     area.trim(),
      landmark: landmark.trim(),
      mobile:   mobile.trim(),
      lat:      userLat,
      lng:      userLng,
      distKm,
      display:  [house.trim(), area.trim(), landmark.trim(), "Akola"].filter(Boolean).join(", "),
    });
  };

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:400, display:"flex", alignItems:"flex-end" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.white, borderRadius:"20px 20px 0 0", width:"100%", maxWidth:540, margin:"0 auto", padding:"20px 18px 44px", maxHeight:"92vh", overflowY:"auto" }}>

        {/* Handle */}
        <div style={{ width:36, height:4, borderRadius:100, background:T.border, margin:"0 auto 20px" }}/>

        {/* Title */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.1rem", letterSpacing:"-0.5px" }}>
            Delivery Address
          </div>
          <button onClick={onClose} style={{ background:T.bg, border:"none", borderRadius:10, width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
            <X size={16} color={T.textSub}/>
          </button>
        </div>

        {/* Address Type */}
        <div style={{ marginBottom:18 }}>
          <div style={{ fontSize:"0.66rem", fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:10 }}>
            Save as
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {ADDR_TYPES.map(({ key, Icon, label }) => {
              const sel = addrType === key;
              return (
                <button key={key} onClick={() => setAddrType(key)}
                  style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6, padding:"11px 8px", borderRadius:14, border:`1.5px solid ${sel ? T.primary : T.border}`, background:sel ? T.primaryLight : T.bg, cursor:"pointer", transition:"all .18s" }}>
                  <Icon size={19} color={sel ? T.primary : T.textMuted} strokeWidth={sel ? 2.2 : 1.8}/>
                  <span style={{ fontSize:"0.72rem", fontWeight:sel ? 700 : 600, color:sel ? T.primary : T.textSub }}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* GPS Button */}
        <button onClick={fetchGPS} disabled={locating}
          style={{ width:"100%", background:T.blueLight, border:`1px solid #bfdbfe`, borderRadius:13, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:T.blue, fontWeight:700, fontSize:"0.84rem", cursor:"pointer", marginBottom:16, opacity:locating ? 0.7 : 1, fontFamily:"inherit" }}>
          {locating
            ? <><Loader2 size={16} style={{ animation:"spin .75s linear infinite" }}/> Fetching your location...</>
            : <><LocateFixed size={16}/> {area ? "📍 Refresh Location" : "📍 Detecting Location..."}</>
          }
        </button>

        {/* House / Flat */}
        <div style={{ marginBottom:13 }}>
          <div style={{ fontSize:"0.66rem", fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:6 }}>
            House / Flat / Building *
          </div>
          <input
            autoFocus
            type="text"
            value={house}
            onChange={e => { setHouse(e.target.value); setErrors(p => ({...p, house:""})); }}
            placeholder="e.g. Flat 302, Sai Residency"
            style={{ width:"100%", border:`1.5px solid ${errors.house ? T.red : house ? T.primary : T.border}`, borderRadius:12, padding:"12px 14px", fontSize:"0.9rem", background:T.bg, color:T.text, outline:"none", transition:"border .15s", fontFamily:"inherit" }}
          />
          {errors.house && <div style={{ color:T.red, fontSize:"0.7rem", marginTop:4, fontWeight:600 }}>{errors.house}</div>}
        </div>

        {/* Area / Street — GPS fills this */}
        <div style={{ marginBottom:13 }}>
          <div style={{ fontSize:"0.66rem", fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:6 }}>
            Area / Locality *
          </div>
          <input
            type="text"
            value={area}
            onChange={e => { setArea(e.target.value); setErrors(p => ({...p, area:""})); }}
            placeholder="e.g. Civil Lines, Ramdaspeth, Akola"
            style={{ width:"100%", border:`1.5px solid ${errors.area ? T.red : area ? T.primary : T.border}`, borderRadius:12, padding:"12px 14px", fontSize:"0.9rem", background:T.bg, color:T.text, outline:"none", transition:"border .15s", fontFamily:"inherit" }}
          />
          {errors.area && <div style={{ color:T.red, fontSize:"0.7rem", marginTop:4, fontWeight:600 }}>{errors.area}</div>}
          {area && !errors.area && (
            <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:4 }}>
              <MapPin size={11} color={T.green}/>
              <span style={{ fontSize:"0.7rem", color:T.green, fontWeight:600 }}>Delivery available in this area</span>
            </div>
          )}
        </div>

        {/* Landmark */}
        <div style={{ marginBottom:13 }}>
          <div style={{ fontSize:"0.66rem", fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:6 }}>
            Landmark <span style={{ fontWeight:400, color:T.textMuted, textTransform:"none", letterSpacing:0 }}>(optional)</span>
          </div>
          <input
            type="text"
            value={landmark}
            onChange={e => setLandmark(e.target.value)}
            placeholder="e.g. Near SBI Bank, Opposite Bus Stand"
            style={{ width:"100%", border:`1.5px solid ${landmark ? T.primary : T.border}`, borderRadius:12, padding:"12px 14px", fontSize:"0.9rem", background:T.bg, color:T.text, outline:"none", transition:"border .15s", fontFamily:"inherit" }}
          />
        </div>

        {/* Mobile Number */}
        <div style={{ marginBottom:22 }}>
          <div style={{ fontSize:"0.66rem", fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:"0.8px", marginBottom:6 }}>
            Mobile Number *
          </div>
          <div style={{ display:"flex", alignItems:"center", border:`1.5px solid ${errors.mobile ? T.red : mobile ? T.primary : T.border}`, borderRadius:12, background:T.bg, overflow:"hidden", transition:"border .15s" }}>
            <span style={{ padding:"12px 10px 12px 14px", fontSize:"0.9rem", color:T.textSub, fontWeight:600, borderRight:`1px solid ${T.border}`, flexShrink:0 }}>+91</span>
            <input
              type="tel"
              value={mobile}
              onChange={e => { setMobile(e.target.value.replace(/\D/g,"").slice(0,10)); setErrors(p => ({...p, mobile:""})); }}
              placeholder="Delivery partner will call on this number"
              style={{ flex:1, border:"none", background:"transparent", padding:"12px 14px", fontSize:"0.9rem", color:T.text, outline:"none", fontFamily:"inherit" }}
            />
          </div>
          {errors.mobile
            ? <div style={{ color:T.red, fontSize:"0.7rem", marginTop:4, fontWeight:600 }}>{errors.mobile}</div>
            : <div style={{ color:T.textMuted, fontSize:"0.68rem", marginTop:4 }}>📞 Delivery partner will call on this number</div>
          }
        </div>

        {/* Confirm */}
        <button onClick={handleConfirm}
          style={{ width:"100%", background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, border:"none", borderRadius:14, padding:"14px", fontWeight:800, fontSize:"0.94rem", cursor:"pointer", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:"0 6px 20px rgba(249,115,22,0.32)", fontFamily:"inherit" }}>
          <MapPin size={16}/> Save & Continue
        </button>
      </div>
    </div>
  );
}

// ── MAIN CART PAGE ────────────────────────────────────────────
export default function CartPage() {
  const navigate = useNavigate();
  const { cart, updateQty, removeItem, clearCart } = useCart();
  const { isLoggedIn, getToken }                    = useAuth();

  const [couponInput,    setCouponInput]    = useState("");
  const [appliedCoupon,  setApplied]        = useState(null);
  const [couponError,    setCouponError]    = useState("");
  const [showCoupons,    setShowCoupons]    = useState(false);
  const [payment,        setPayment]        = useState("cod");
  const [note,           setNote]           = useState("");
  const [tip,            setTip]            = useState(0);
  const [placing,        setPlacing]        = useState(false);
  const [placed,         setPlaced]         = useState(false);
  const [placeError,     setPlaceError]     = useState("");
  const [placedRestName, setPlacedRestName] = useState("");
  const [placedOrderId,  setPlacedOrderId]  = useState("");

  // Address state — structured
  const [address,        setAddress]        = useState(null);   // selected address
  const [savedAddresses, setSavedAddresses] = useState([]);     // backend se loaded
  const [showAddrSheet,  setShowAddrSheet]  = useState(false);
  const [addrLoading,    setAddrLoading]    = useState(false);

  // ── Wallet state ──────────────────────────────────────────
  const [walletBalance,  setWalletBalance]  = useState(0);
  const [walletLoading,  setWalletLoading]  = useState(false);

  // ── Coupons state ─────────────────────────────────────────
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponsLoading,   setCouponsLoading]   = useState(false);

  // ── Load coupons from API ──────────────────────────────────
  useEffect(() => {
    const restaurantId = cart[0]?.restaurantId || cart[0]?.restaurant || "";
    const fetchCoupons = async () => {
      setCouponsLoading(true);
      try {
        const url = restaurantId
          ? `${API}/coupons/public?restaurant=${restaurantId}`
          : `${API}/coupons/public`;
        const res  = await fetch(url);
        const data = await res.json();
        if (data.success && Array.isArray(data.coupons)) {
          setAvailableCoupons(data.coupons);
        }
      } catch (e) { console.error("Coupons fetch error:", e); }
      finally { setCouponsLoading(false); }
    };
    fetchCoupons();
  }, [cart]);

  // ── Load wallet balance ────────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchWallet = async () => {
      setWalletLoading(true);
      try {
        const res  = await fetch(`${API}/wallet/balance`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.success) setWalletBalance(data.balance || 0);
      } catch (e) { console.error("Wallet fetch error:", e); }
      finally { setWalletLoading(false); }
    };
    fetchWallet();
  }, [isLoggedIn]);

  // ── Load saved addresses on mount ─────────────────────────
  useEffect(() => {
    if (!isLoggedIn) return;
    const loadAddresses = async () => {
      setAddrLoading(true);
      try {
        const res  = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        if (data.success && data.user?.addresses?.length > 0) {
          const addrs = data.user.addresses;
          setSavedAddresses(addrs);
          // Auto-select default address
          const def = addrs.find(a => a.isDefault) || addrs[0];
          if (def && !address) {
            setAddress({
              _id:      def._id,
              type:     def.type     || "Home",
              house:    def.house    || def.line1 || "",
              area:     def.area     || "",
              landmark: def.landmark || "",
              display:  def.display  || [def.house || def.line1, def.area, "Akola"].filter(Boolean).join(", "),
              isDefault: def.isDefault,
              lat:      def.lat  || null,   // ✅ saved address ka GPS lat
              lng:      def.lng  || null,   // ✅ saved address ka GPS lng
              distKm:   def.distKm || 3,
            });
          }
        }
      } catch (e) { console.error("Address load error:", e); }
      finally { setAddrLoading(false); }
    };
    loadAddresses();
  }, [isLoggedIn]);

  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const isMobile = width < 768;
  const isDesk   = width >= 1024;
  const px       = isMobile ? 12 : 20;

  // ── Bill calculation ─────────────────────────────────────────
  // Delivery: ₹10/km — GPS se distance milti hai, warna 3km default
  const sub    = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const distKm = address?.distKm ?? 3;        // GPS km, default 3km
  const delFee = Math.round(distKm * 10);     // ₹10 per km
  const disc   = appliedCoupon ? appliedCoupon.discount(sub) : 0;
  const total  = sub + delFee + tip - disc;

  // ── Coupon ─────────────────────────────────────────────────
  const applyCoupon = (code) => {
    const c     = code.trim().toUpperCase();
    const found = availableCoupons.find(cp => cp.code === c);
    if (!found)                  { setCouponError("Invalid coupon code");                  return; }
    if (sub < found.minOrder)    { setCouponError(`Min order ₹${found.minOrder} needed`);  return; }
    if (found.isActive === false)    { setCouponError("This coupon is no longer active");       return; }

    // Discount compute karo — model fields: type + value
    const computeDiscount = (orderSub) => {
      if (found.type === "percent") {
        return Math.min(Math.round(orderSub * found.value / 100), found.maxDiscount || orderSub);
      }
      if (found.type === "free_delivery") return 30;
      return found.value; // flat
    };

    setApplied({ ...found, discount: computeDiscount });
    setCouponError("");
    setShowCoupons(false);
  };
  const removeCoupon = () => { setApplied(null); setCouponInput(""); setCouponError(""); };

  // ── Place Order ────────────────────────────────────────────
  const placeOrder = async () => {
    if (!isLoggedIn) { navigate("/login", { state: { from: "/cart" } }); return; }
    if (!address)    { setPlaceError("Delivery address add karo!"); setShowAddrSheet(true); return; }
    if (cart.length === 0) return;

    // ✅ Wallet balance check
    if (payment === "wallet" && walletBalance < total) {
      setPlaceError(`Wallet balance kam hai! Available: ₹${walletBalance}, Required: ₹${total}. Profile mein wallet top-up karo.`);
      return;
    }

    setPlacing(true);
    setPlaceError("");

    try {
      const restaurantId = cart[0]?.restaurantId || cart[0]?.restaurant || "";
      const storeId      = cart[0]?.storeId      || cart[0]?.store      || "";
      const restName     = cart[0]?.restaurantName || cart[0]?.storeName || "";
      const serviceType  = cart[0]?.serviceType || (storeId ? "grocery" : "food");

      const orderPayload = {
        ...(storeId      ? { store:      storeId }      : {}),
        ...(restaurantId ? { restaurant: restaurantId } : {}),
        serviceType,
        items: cart.map(i => ({
          menuItem: i._id || i.id,
          name:     i.name,
          price:    Number(i.price),
          qty:      Number(i.qty),
        })),
        deliveryAddress: {
          type:     address.type,
          line1:    address.house,
          area:     address.area,
          landmark: address.landmark,
          city:     "Akola",
          full:     address.display,
          phone:    address.mobile || "",
          lat:      address.lat  || null,
          lng:      address.lng  || null,
        },
        paymentMode: payment,   // "cod" ya "wallet"
        tip:         Number(tip) || 0,
        note,
      };

      const res  = await fetch(`${API}/orders`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` },
        body:    JSON.stringify(orderPayload),
      });
      const data = await res.json();
      if (data.success) {
        clearCart();
        setPlacedRestName(restName);
        setPlaced(true);
        // Wallet balance refresh karo agar wallet se pay hua
        if (payment === "wallet") setWalletBalance(prev => prev - total);
      } else {
        setPlaceError(data.message || "Order place nahi hua. Try again!");
      }

    } catch {
      setPlaceError("Server se connect nahi ho paya.");
    } finally {
      setPlacing(false);
    }
  };

  // ── Order placed ───────────────────────────────────────────
  if (placed) return (
    <OrderPlaced
      restaurantName={placedRestName}
      onGoHome={() => navigate("/")}
      onTrack={() => navigate("/orders", { state:{ orderId: placedOrderId } })}
    />
  );

  // ── Empty cart ─────────────────────────────────────────────
  if (cart.length === 0) return (
    <div style={{ minHeight:"100vh", background:T.bg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,900&family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');`}</style>
      <ShoppingBag size={64} color={T.border} strokeWidth={1} style={{ marginBottom:18 }}/>
      <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.5rem", marginBottom:6 }}>Cart is empty</div>
      <div style={{ color:T.textSub, fontSize:"0.86rem", marginBottom:24, textAlign:"center" }}>Add items to get started</div>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", justifyContent:"center" }}>
        <button onClick={() => navigate("/restaurants")}
          style={{ background:T.primary, color:"#fff", border:"none", borderRadius:100, padding:"12px 24px", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
          🍔 Food
        </button>
        <button onClick={() => navigate("/grocery")}
          style={{ background:"#16a34a", color:"#fff", border:"none", borderRadius:100, padding:"12px 24px", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
          🛒 Grocery
        </button>
      </div>
    </div>
  );

  const restName = cart[0]?.restaurantName || cart[0]?.storeName || "Your Order";

  return (
    <div style={{ background:T.bg, minHeight:"100vh", fontFamily:"'Plus Jakarta Sans','Outfit',sans-serif", color:T.text, overflowX:"hidden", paddingBottom:88 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,700;9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{background:${T.bg};-webkit-tap-highlight-color:transparent;}
        input,textarea,button{font-family:inherit;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spin{animation:spin .75s linear infinite;}
        textarea:focus,input:focus{outline:none;border-color:${T.primary}!important;box-shadow:0 0 0 3px rgba(249,115,22,0.09)!important;}
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ position:"sticky", top:0, zIndex:100, background:T.white, borderBottom:`1px solid ${T.border}`, padding:`13px ${px+4}px`, boxShadow:"0 1px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, maxWidth:900, margin:"0 auto" }}>
          <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", cursor:"pointer", color:T.text, display:"flex", padding:4 }}>
            <ArrowLeft size={21}/>
          </button>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:"1.15rem", letterSpacing:"-0.5px" }}>Your Cart</div>
            <div style={{ fontSize:"0.7rem", color:T.textSub }}>{restName} · Akola</div>
          </div>
          <div style={{ background:T.primaryLight, border:`1px solid ${T.primaryMid}`, color:T.primary, fontSize:"0.72rem", fontWeight:700, padding:"4px 10px", borderRadius:100 }}>
            {cart.reduce((s,i) => s+i.qty, 0)} items
          </div>
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ maxWidth:900, margin:"0 auto", padding:`12px ${px}px 0`, display:"grid", gridTemplateColumns:isDesk?"1fr 340px":"1fr", gap:10, alignItems:"start" }}>
        <div>

          {/* ── 1. DELIVERY ADDRESS ── */}
          <Card>
            <SLabel>Delivery Address</SLabel>
            <div style={{ padding:"10px 16px 14px" }}>

              {/* Loading */}
              {addrLoading && (
                <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 0", color:T.textSub, fontSize:"0.82rem" }}>
                  <Loader2 size={15} style={{ animation:"spin .75s linear infinite" }}/> Loading saved addresses...
                </div>
              )}

              {/* Saved addresses list */}
              {!addrLoading && savedAddresses.length > 0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:10 }}>
                  {savedAddresses.map((a) => {
                    const addrObj = { _id:a._id, type:a.type||"Home", house:a.house||a.line1||"", area:a.area||"", landmark:a.landmark||"", display:a.display||[a.house||a.line1, a.area, "Akola"].filter(Boolean).join(", "), isDefault:a.isDefault };
                    const isSelected = address?._id === a._id || (!address?._id && a.isDefault);
                    return (
                      <div key={a._id} onClick={() => setAddress(addrObj)}
                        style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"11px 12px", borderRadius:12, border:`1.5px solid ${isSelected ? T.primary : T.border}`, background:isSelected ? T.primaryLight : T.white, cursor:"pointer", transition:"all .18s" }}>
                        <div style={{ width:36, height:36, borderRadius:10, background:isSelected ? T.primaryMid : T.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {a.type==="Home"  && <HomeIcon   size={16} color={isSelected?T.primary:T.textMuted}/>}
                          {a.type==="Work"  && <Briefcase  size={16} color={isSelected?T.primary:T.textMuted}/>}
                          {(!a.type||a.type==="Other") && <Navigation size={16} color={isSelected?T.primary:T.textMuted}/>}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                            <span style={{ fontWeight:700, fontSize:"0.82rem", color:isSelected?T.primary:T.text }}>{a.type||"Other"}</span>
                            {a.isDefault && <span style={{ background:T.primaryMid, color:T.primaryDark, fontSize:"0.56rem", fontWeight:800, padding:"1px 6px", borderRadius:100 }}>DEFAULT</span>}
                          </div>
                          <div style={{ fontSize:"0.82rem", color:T.text, fontWeight:500, lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.house||a.line1}</div>
                          <div style={{ fontSize:"0.75rem", color:T.textSub, lineHeight:1.4 }}>{a.area}{a.landmark ? ` · Near ${a.landmark}` : ""}</div>
                        </div>
                        {/* Radio */}
                        <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${isSelected?T.primary:T.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}>
                          {isSelected && <div style={{ width:8, height:8, borderRadius:"50%", background:T.primary }}/>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* No saved address — show add button */}
              {!addrLoading && savedAddresses.length === 0 && !address && (
                <button onClick={() => setShowAddrSheet(true)}
                  style={{ width:"100%", background:T.primaryLight, border:`1.5px dashed ${T.primary}`, borderRadius:12, padding:"13px", display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:T.primary, fontWeight:700, fontSize:"0.86rem", cursor:"pointer", fontFamily:"inherit", marginBottom:4 }}>
                  <MapPin size={16}/> Add Delivery Address
                </button>
              )}

              {/* Add new address button */}
              {!addrLoading && (savedAddresses.length > 0) && (
                <button onClick={() => setShowAddrSheet(true)}
                  style={{ width:"100%", background:T.white, border:`1.5px dashed ${T.border}`, borderRadius:10, padding:"9px", display:"flex", alignItems:"center", justifyContent:"center", gap:6, color:T.primary, fontWeight:600, fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit" }}>
                  <Plus size={14}/> Add New Address
                </button>
              )}
            </div>

            {/* Delivery strip */}
            <div style={{ background:T.primaryLight, borderTop:`1px solid ${T.primaryMid}`, padding:"9px 16px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
              <Clock size={13} color={T.primary}/>
              <span style={{ fontSize:"0.75rem", fontWeight:600, color:T.primaryDark }}>Delivery in <strong>28–35 min</strong></span>
              <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:4 }}>
                <Bike size={12} color={T.primary}/>
                <span style={{ fontSize:"0.7rem", color:T.primary, fontWeight:700 }}>
                  {`₹${delFee} delivery (${distKm} km)`}
                </span>
              </div>
            </div>
          </Card>

          {/* ── 2. CART ITEMS ── */}
          <Card>
            <SLabel>Order Items</SLabel>
            <div style={{ paddingTop:6 }}>
              {cart.map((item, idx) => (
                <div key={item._id || item.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 16px", borderBottom:idx<cart.length-1?`1px solid ${T.border}`:"none" }}>
                  <VegDot isVeg={item.isVeg}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:"0.85rem", color:T.text, lineHeight:1.3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.name}</div>
                    <div style={{ fontWeight:600, fontSize:"0.78rem", color:T.textSub, marginTop:2 }}>₹{item.price}</div>
                  </div>
                  {/* Qty stepper */}
                  <div style={{ display:"flex", alignItems:"center", border:`1.5px solid ${T.primary}`, borderRadius:9, overflow:"hidden", flexShrink:0 }}>
                    <button onClick={() => updateQty(item._id||item.id, item.qty-1)}
                      style={{ background:"none", border:"none", color:T.primary, padding:"5px 9px", cursor:"pointer", display:"flex" }}>
                      {item.qty===1 ? <Trash2 size={13}/> : <Minus size={13}/>}
                    </button>
                    <span style={{ fontWeight:700, fontSize:"0.82rem", color:T.primary, minWidth:18, textAlign:"center" }}>{item.qty}</span>
                    <button onClick={() => updateQty(item._id||item.id, item.qty+1)}
                      style={{ background:"none", border:"none", color:T.primary, padding:"5px 9px", cursor:"pointer", display:"flex" }}>
                      <Plus size={13}/>
                    </button>
                  </div>
                  <div style={{ fontWeight:700, fontSize:"0.88rem", color:T.text, minWidth:44, textAlign:"right", flexShrink:0 }}>
                    ₹{item.price*item.qty}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding:"10px 16px 14px" }}>
              <button onClick={() => navigate(-1)}
                style={{ background:"none", border:`1.5px dashed ${T.primary}`, color:T.primary, borderRadius:10, padding:"9px 14px", fontWeight:600, fontSize:"0.78rem", cursor:"pointer", display:"flex", alignItems:"center", gap:6, width:"100%" }}>
                <Plus size={14}/> Add more items
              </button>
            </div>
          </Card>

          {/* ── 3. COOKING INSTRUCTIONS ── */}
          <Card>
            <SLabel>Cooking Instructions</SLabel>
            <div style={{ padding:"10px 16px 14px" }}>
              <textarea rows={3} placeholder="e.g. Less spicy, no onion, extra sauce..."
                value={note} onChange={e=>setNote(e.target.value)}
                style={{ width:"100%", resize:"none", background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:10, padding:"10px 12px", color:T.text, fontSize:"0.83rem", lineHeight:1.6, transition:"border .2s" }}/>
              <div style={{ fontSize:"0.68rem", color:T.textMuted, marginTop:5 }}>We'll pass this to the restaurant.</div>
            </div>
          </Card>

          {/* ── 4. COUPON ── */}
          <Card>
            <SLabel>Coupons & Offers</SLabel>
            <div style={{ padding:"10px 16px 14px" }}>
              {appliedCoupon ? (
                <div style={{ background:T.greenLight, border:`1.5px solid ${T.green}`, borderRadius:12, padding:"12px 14px", display:"flex", alignItems:"center", gap:10 }}>
                  <CheckCircle2 size={17} color={T.green} style={{ flexShrink:0 }}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:"0.82rem", color:T.green }}>{appliedCoupon.code} applied!</div>
                    <div style={{ fontSize:"0.72rem", color:T.green, opacity:0.85 }}>You save ₹{disc}</div>
                  </div>
                  <button onClick={removeCoupon} style={{ background:"none", border:"none", cursor:"pointer", color:T.green, display:"flex" }}>
                    <X size={17}/>
                  </button>
                </div>
              ) : (
                <>
                  <div style={{ display:"flex", gap:8, marginBottom:couponError?7:10 }}>
                    <input style={{ flex:1, background:T.bg, border:`1.5px solid ${T.border}`, borderRadius:10, padding:"10px 12px", color:T.text, fontSize:"0.85rem" }}
                      placeholder="Enter coupon code" value={couponInput}
                      onChange={e=>{setCouponInput(e.target.value.toUpperCase());setCouponError("");}}/>
                    <button onClick={()=>applyCoupon(couponInput)} disabled={!couponInput}
                      style={{ background:couponInput?T.primary:T.border, color:couponInput?"#fff":T.textMuted, border:"none", borderRadius:10, padding:"10px 18px", fontWeight:700, fontSize:"0.83rem", cursor:couponInput?"pointer":"not-allowed", whiteSpace:"nowrap" }}>
                      Apply
                    </button>
                  </div>
                  {couponError && (
                    <div style={{ display:"flex", alignItems:"center", gap:5, color:T.red, fontSize:"0.72rem", marginBottom:8, fontWeight:600 }}>
                      <AlertCircle size={12}/> {couponError}
                    </div>
                  )}
                  <button onClick={()=>setShowCoupons(s=>!s)}
                    style={{ background:"none", border:"none", cursor:"pointer", color:T.primary, fontSize:"0.8rem", fontWeight:700, display:"flex", alignItems:"center", gap:5, padding:0 }}>
                    <Gift size={13}/> View available offers
                    <ChevronDown size={12} style={{ transform:showCoupons?"rotate(180deg)":"none", transition:"transform .2s" }}/>
                  </button>
                  {showCoupons && (
                    <div style={{ marginTop:10, display:"flex", flexDirection:"column", gap:8 }}>
                      {couponsLoading ? (
                        <div style={{ display:"flex", alignItems:"center", gap:8, color:T.textSub, fontSize:"0.8rem", padding:"8px 0" }}>
                          <Loader2 size={14} style={{ animation:"spin .75s linear infinite" }}/> Loading offers...
                        </div>
                      ) : availableCoupons.length === 0 ? (
                        <div style={{ color:T.textMuted, fontSize:"0.78rem", padding:"8px 0" }}>
                          No offers available right now.
                        </div>
                      ) : (
                        availableCoupons.map(c => (
                          <div key={c.code} style={{ border:`1.5px dashed ${T.primaryMid}`, borderRadius:10, padding:"11px 12px", display:"flex", alignItems:"flex-start", gap:10 }}>
                            <BadgePercent size={16} color={T.primary} style={{ flexShrink:0, marginTop:1 }}/>
                            <div style={{ flex:1 }}>
                              <div style={{ fontWeight:700, fontSize:"0.8rem", color:T.text }}>{c.description || c.desc}</div>
                              <div style={{ fontSize:"0.68rem", color:T.textSub, marginTop:2 }}>Code: <strong>{c.code}</strong> · Min ₹{c.minOrder}</div>
                            </div>
                            <button onClick={()=>{setCouponInput(c.code);applyCoupon(c.code);}}
                              style={{ background:T.primaryLight, border:`1px solid ${T.primaryMid}`, color:T.primary, borderRadius:7, padding:"5px 10px", fontWeight:700, fontSize:"0.72rem", cursor:"pointer", flexShrink:0 }}>
                              Apply
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* ── 5. TIP ── */}
          <Card>
            <SLabel>Tip Your Delivery Partner</SLabel>
            <div style={{ padding:"10px 16px 14px" }}>
              <div style={{ color:T.textSub, fontSize:"0.73rem", marginBottom:10 }}>100% goes directly to your delivery partner</div>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {[0,10,20,30,50].map(amt => (
                  <button key={amt} onClick={()=>setTip(amt)}
                    style={{ flex:"1 1 50px", background:tip===amt?T.primaryLight:T.white, border:`1.5px solid ${tip===amt?T.primary:T.border}`, color:tip===amt?T.primary:T.textSub, borderRadius:100, padding:"7px 6px", fontWeight:700, fontSize:"0.78rem", cursor:"pointer", transition:"all .2s", whiteSpace:"nowrap", textAlign:"center" }}>
                    {amt===0?"No tip":`₹${amt}`}
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* ── 6. PAYMENT ── */}
          <Card>
            <SLabel>Payment Method</SLabel>
            <div style={{ paddingTop:4 }}>
              {PAYMENT_METHODS.map((pm, i) => {
                const Icon = pm.icon;
                const sel  = payment===pm.id;
                const isWallet = pm.id === "wallet";
                return (
                  <div key={pm.id} onClick={()=>setPayment(pm.id)}
                    style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", borderTop:i===0?"none":`1px solid ${T.border}`, cursor:"pointer", background:sel?T.primaryLight:"transparent", transition:"background .15s" }}>
                    <div style={{ width:40, height:40, borderRadius:12, background:sel?T.primaryMid:T.bg, display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${sel?T.primaryMid:T.border}`, flexShrink:0, transition:"all .2s" }}>
                      <Icon size={17} color={sel?T.primary:T.textSub} strokeWidth={1.8}/>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:sel?700:600, fontSize:"0.85rem", color:sel?T.primary:T.text }}>
                        {pm.label}
                        {isWallet && (
                          <span style={{ marginLeft:8, fontSize:"0.75rem", fontWeight:800, color: walletBalance >= total ? T.green : T.red }}>
                            ₹{walletLoading ? "..." : walletBalance}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize:"0.7rem", color:T.textSub, marginTop:1 }}>{pm.sub}</div>
                    </div>
                    <div style={{ width:19, height:19, borderRadius:"50%", border:`2px solid ${sel?T.primary:T.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      {sel && <div style={{ width:9, height:9, borderRadius:"50%", background:T.primary }}/>}
                    </div>
                  </div>
                );
              })}
              {/* Wallet low balance warning */}
              {payment === "wallet" && walletBalance < total && (
                <div style={{ margin:"0 12px 12px", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"10px 12px", display:"flex", alignItems:"flex-start", gap:8 }}>
                  <AlertCircle size={14} color={T.red} style={{ flexShrink:0, marginTop:1 }}/>
                  <div style={{ fontSize:"0.75rem", color:T.red, lineHeight:1.5 }}>
                    Wallet balance kam hai! Required: <strong>₹{total}</strong>, Available: <strong>₹{walletBalance}</strong>.<br/>
                    Profile pe jaake wallet top-up karo.
                  </div>
                </div>
              )}
              <div style={{ height:8 }}/>
            </div>
          </Card>

          {/* Error */}
          {placeError && (
            <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:12, padding:"10px 14px", marginBottom:10, display:"flex", alignItems:"center", gap:8 }}>
              <AlertCircle size={15} color={T.red} style={{ flexShrink:0 }}/>
              <span style={{ fontSize:"0.82rem", color:T.red, fontWeight:600 }}>{placeError}</span>
            </div>
          )}
        </div>

        {/* ── RIGHT: BILL SUMMARY (desktop) ── */}
        <div style={{ position:isDesk?"sticky":"static", top:isDesk?76:"auto" }}>
          <Card>
            <SLabel>Bill Summary</SLabel>
            <div style={{ padding:"12px 16px 14px" }}>
              {[
                { label:"Item Total",                       value:`₹${sub}`,   green:false },
                { label:`Delivery (${distKm} km × ₹10)`,   value:`₹${delFee}`, green:false },
                ...(tip>0  ?[{ label:"Delivery Tip",       value:`₹${tip}`,   green:false }]:[]),
                ...(disc>0 ?[{ label:`Coupon (${appliedCoupon?.code})`, value:`-₹${disc}`, green:true }]:[]),
              ].map((row,i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:9 }}>
                  <span style={{ color:T.textSub, fontSize:"0.81rem" }}>{row.label}</span>
                  <span style={{ fontWeight:600, fontSize:"0.82rem", color:row.green?T.green:T.text }}>{row.value}</span>
                </div>
              ))}
              <div style={{ height:1, background:T.border, margin:"10px 0" }}/>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <span style={{ fontWeight:800, fontSize:"0.95rem" }}>To Pay</span>
                <span style={{ fontWeight:800, fontSize:"1rem" }}>₹{total}</span>
              </div>
              {disc>0 && (
                <div style={{ marginTop:10, background:T.greenLight, borderRadius:9, padding:"7px 10px", display:"flex", alignItems:"center", gap:6 }}>
                  <CheckCircle2 size={13} color={T.green}/>
                  <span style={{ fontSize:"0.72rem", fontWeight:700, color:T.green }}>Saving ₹{disc} on this order!</span>
                </div>
              )}
            </div>
          </Card>

          <Card>
            <div style={{ padding:"12px 16px", display:"flex", alignItems:"flex-start", gap:10 }}>
              <Shield size={15} color={T.green} style={{ flexShrink:0, marginTop:1 }}/>
              <div style={{ fontSize:"0.72rem", color:T.textSub, lineHeight:1.55 }}>
                <strong style={{ color:T.text }}>Safe & Secure.</strong> 100% authentic food, carefully handled & packaged.
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* ── STICKY PLACE ORDER BAR ── */}
      <div style={{ position:"fixed", bottom:0, left:0, right:0, background:T.white, borderTop:`1px solid ${T.border}`, padding:`11px ${px+4}px`, zIndex:150, boxShadow:"0 -4px 20px rgba(0,0,0,0.06)" }}>
        <div style={{ maxWidth:900, margin:"0 auto", display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ flexShrink:0 }}>
            <div style={{ fontWeight:800, fontSize:"1rem", color:T.text, lineHeight:1.2 }}>₹{total}</div>
            <div style={{ fontSize:"0.65rem", color:T.textSub }}>Total amount</div>
          </div>
          <button onClick={placeOrder} disabled={placing}
            style={{ flex:1, background:`linear-gradient(135deg,${T.primaryDark},${T.primary})`, color:"#fff", border:"none", borderRadius:12, padding:"14px 12px", fontWeight:800, fontSize:"0.95rem", cursor:placing?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, boxShadow:"0 5px 18px rgba(249,115,22,0.28)", opacity:placing?0.85:1 }}>
            {placing
              ? <><Loader2 size={18} className="spin"/> Placing order...</>
              : isLoggedIn
                ? <><Banknote size={16}/> Place Order · ₹{total}</>
                : <><Banknote size={16}/> Login to Place Order</>
            }
          </button>
        </div>
      </div>

      {/* ── ADDRESS SHEET ── */}
      {showAddrSheet && (
        <AddressSheet
          initialAddress={address}
          onClose={() => setShowAddrSheet(false)}
          onConfirm={async (addr) => {
            setAddress(addr);
            setShowAddrSheet(false);
            setPlaceError("");
            // Save to backend — next baar auto-load hoga
            if (isLoggedIn) {
              try {
                const res = await fetch(`${API}/auth/add-address`, {
                  method: "POST",
                  headers: { "Content-Type":"application/json", Authorization:`Bearer ${getToken()}` },
                  body: JSON.stringify(addr),
                });
                const data = await res.json();
                if (data.success && data.address) {
                  const saved = { ...data.address, _id: data.address._id };
                  setSavedAddresses(prev => {
                    const exists = prev.some(a => a._id?.toString() === saved._id?.toString());
                    return exists ? prev : [...prev, saved];
                  });
                  setAddress(prev => ({ ...prev, _id: data.address._id }));
                }
              } catch (e) { /* silent fail */ }
            }
          }}
        />
      )}
    </div>
  );
}